import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, Search, DollarSign, Building2, Users as UsersIcon, Mail, MessageCircle, Bot, Code } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import TemplateSignatureGuide from "@/components/TemplateSignatureGuide";

const ROLES = [
  { v: "admin", label: "Administrator" },
  { v: "doctor", label: "Mjek Stomatolog" },
  { v: "receptionist", label: "Recepsionist" },
  { v: "assistant", label: "Asistent" },
];

export default function Settings() {
  const { user: me } = useAuth();
  const { settings, options, currency, symbol, reload: reloadCurrency, format } = useCurrency();

  // ─── Tab state ───
  const [tab, setTab] = useState("currency");

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="label-eyebrow">Administrim</div>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Cilësimet</h1>
        <p className="mt-2 text-sm text-[#5C5C5C]">Konfiguro çmimet, monedhën, klinikën dhe përdoruesit.</p>
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 border-b border-[#E2DFD8] flex-wrap">
        <TabBtn active={tab === "currency"} onClick={() => setTab("currency")} icon={DollarSign} label="Monedha & Çmimet" testId="tab-currency" />
        <TabBtn active={tab === "clinic"} onClick={() => setTab("clinic")} icon={Building2} label="Klinika" testId="tab-clinic" />
        <TabBtn active={tab === "email"} onClick={() => setTab("email")} icon={Mail} label="Email (SMTP)" testId="tab-email" />
        <TabBtn active={tab === "whatsapp"} onClick={() => setTab("whatsapp")} icon={MessageCircle} label="WhatsApp" testId="tab-whatsapp" />
        <TabBtn active={tab === "ai"} onClick={() => setTab("ai")} icon={Bot} label="AI Chat" testId="tab-ai" />
        <TabBtn active={tab === "embed"} onClick={() => setTab("embed")} icon={Code} label="Embed Code" testId="tab-embed" />
        <TabBtn active={tab === "users"} onClick={() => setTab("users")} icon={UsersIcon} label="Përdoruesit" testId="tab-users" />
      </div>

      {tab === "currency" && (
        <CurrencyAndPrices
          settings={settings}
          options={options}
          currency={currency}
          symbol={symbol}
          format={format}
          reloadCurrency={reloadCurrency}
        />
      )}
      {tab === "clinic" && <ClinicInfo settings={settings} reload={reloadCurrency} />}
      {tab === "email" && <EmailSmtpConfig settings={settings} reload={reloadCurrency} />}
      {tab === "whatsapp" && <WhatsAppConfig settings={settings} reload={reloadCurrency} />}
      {tab === "ai" && <AIChatConfig settings={settings} reload={reloadCurrency} />}
      {tab === "embed" && <EmbedCodeTab />}
      {tab === "users" && <UsersAdmin me={me} />}
    </div>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-2.5 -mb-px text-sm font-medium border-b-2 transition-colors ${
        active
          ? "border-[#2C4C3B] text-[#2C4C3B]"
          : "border-transparent text-[#5C5C5C] hover:text-[#2C4C3B]"
      }`}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

/* ─────────────────────────────────────────────
   1) Currency + Bulk Procedure Price Editing
   ───────────────────────────────────────────── */
function CurrencyAndPrices({ settings, options, currency, symbol, format, reloadCurrency }) {
  const [savingCur, setSavingCur] = useState(false);
  const [procedures, setProcedures] = useState([]);
  const [edits, setEdits] = useState({}); // {id: newCost}
  const [search, setSearch] = useState("");
  const [savingPrices, setSavingPrices] = useState(false);

  const loadProcedures = async () => {
    const { data } = await api.get("/procedures");
    setProcedures(data);
    setEdits({});
  };
  useEffect(() => { loadProcedures(); }, []);

  const changeCurrency = async (cur) => {
    if (cur === currency) return;
    if (!window.confirm(`Ndrysho monedhën në ${options[cur]?.name || cur}? Çmimet ekzistuese nuk konvertohen automatikisht — i përditëson manualisht më poshtë.`)) return;
    setSavingCur(true);
    try {
      await api.put("/settings", { currency: cur });
      await reloadCurrency();
      toast.success(`Monedha u ndryshua në ${options[cur]?.name || cur}`);
    } catch (e) {
      toast.error("Gabim gjatë ndryshimit");
    } finally {
      setSavingCur(false);
    }
  };

  const categories = useMemo(
    () => Array.from(new Set(procedures.map((p) => p.category))).sort(),
    [procedures]
  );
  const [catFilter, setCatFilter] = useState("all");

  const filtered = procedures.filter((p) => {
    const matchCat = catFilter === "all" || p.category === catFilter;
    const matchSearch = !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.code || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const handleEdit = (id, value) => setEdits({ ...edits, [id]: value });

  const savePrices = async () => {
    const items = Object.entries(edits)
      .filter(([_id, v]) => v !== "" && !isNaN(Number(v)))
      .map(([id, default_cost]) => ({ id, default_cost: Number(default_cost) }));
    if (items.length === 0) {
      toast.info("Asnjë çmim i ndryshuar.");
      return;
    }
    setSavingPrices(true);
    try {
      const { data } = await api.put("/procedures/bulk-prices", { items });
      toast.success(`${data.updated} çmime u përditësuan`);
      await loadProcedures();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gabim");
    } finally {
      setSavingPrices(false);
    }
  };

  const editedCount = Object.keys(edits).filter((k) => edits[k] !== "" && !isNaN(Number(edits[k]))).length;

  return (
    <div className="space-y-8" data-testid="currency-prices-section">
      {/* Currency selector */}
      <div className="surface-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign size={16} className="text-[#2C4C3B]" />
          <h2 className="text-lg font-semibold">Monedha Bazë</h2>
        </div>
        <p className="text-sm text-[#5C5C5C] mb-4">
          Monedha aktuale: <span className="font-medium text-[#2C4C3B]">{options[currency]?.name || currency} ({symbol})</span> · Shembull: {format(12500)}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {Object.entries(options).map(([code, opt]) => (
            <button
              key={code}
              onClick={() => changeCurrency(code)}
              disabled={savingCur}
              data-testid={`currency-option-${code}`}
              className={`rounded-lg border-2 px-3 py-3 text-sm transition-colors ${
                code === currency
                  ? "border-[#2C4C3B] bg-[#2C4C3B] text-[#FDFBF7]"
                  : "border-[#E2DFD8] text-[#5C5C5C] hover:border-[#2C4C3B]"
              }`}
            >
              <div className="text-lg font-bold">{opt.symbol}</div>
              <div className="text-[10px] uppercase tracking-wider mt-0.5">{code}</div>
              <div className="text-[10px] mt-0.5 opacity-80">{opt.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Bulk price editing */}
      <div className="surface-card p-6">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold">Çmimet e Procedurave ({symbol})</h2>
            <p className="text-sm text-[#5C5C5C] mt-1">Përditëso çmimet referencë për të gjitha procedurat. Ndryshimet ruhen menjëherë me butonin Ruaj.</p>
          </div>
          <button
            onClick={savePrices}
            disabled={savingPrices || editedCount === 0}
            className="btn-primary disabled:opacity-50"
            data-testid="save-prices-button"
          >
            <Save size={14} />
            {savingPrices ? "Duke ruajtur…" : editedCount > 0 ? `Ruaj ${editedCount} çmim${editedCount === 1 ? "" : "e"}` : "Pa ndryshime"}
          </button>
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
            <Input
              placeholder="Kërko procedurë (emër ose kod)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="price-search-input"
            />
          </div>
          <FilterChip label="Të gjitha" active={catFilter === "all"} onClick={() => setCatFilter("all")} />
          {categories.map((c) => (
            <FilterChip key={c} label={c} active={catFilter === c} onClick={() => setCatFilter(c)} />
          ))}
        </div>

        <div className="overflow-x-auto rounded-lg border border-[#E2DFD8]">
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-left">
              <tr>
                <th className="px-4 py-2.5 label-eyebrow">Kodi</th>
                <th className="px-4 py-2.5 label-eyebrow">Emri</th>
                <th className="px-4 py-2.5 label-eyebrow">Kategoria</th>
                <th className="px-4 py-2.5 label-eyebrow text-right">Çmimi Aktual</th>
                <th className="px-4 py-2.5 label-eyebrow text-right">Çmimi i Ri ({symbol})</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const isEdited = edits[p.id] !== undefined && edits[p.id] !== "" && Number(edits[p.id]) !== p.default_cost;
                return (
                  <tr key={p.id} className="border-t border-[#E2DFD8]" data-testid={`price-row-${p.id}`}>
                    <td className="px-4 py-2 font-mono text-xs text-[#5C5C5C]">{p.code || "—"}</td>
                    <td className="px-4 py-2 font-medium">{p.name}</td>
                    <td className="px-4 py-2">
                      <span className="rounded-full bg-[#EAE4D9] px-2 py-0.5 text-[10px] font-medium text-[#2C4C3B]">{p.category}</span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-[#5C5C5C]">{format(p.default_cost)}</td>
                    <td className="px-4 py-2 text-right">
                      <Input
                        type="number"
                        step="0.01"
                        defaultValue={p.default_cost}
                        onChange={(e) => handleEdit(p.id, e.target.value)}
                        data-testid={`price-input-${p.id}`}
                        className={`w-32 text-right font-mono ml-auto ${isEdited ? "border-[#2C4C3B] bg-[#2C4C3B]/5" : ""}`}
                      />
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[#5C5C5C]">Pa rezultate.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button onClick={onClick}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              active ? "bg-[#2C4C3B] text-[#FDFBF7]" : "bg-white border border-[#E2DFD8] text-[#5C5C5C] hover:text-[#2C4C3B]"
            }`}>{label}</button>
  );
}

/* ─────────────────────────────────────────────
   2) Clinic Info
   ───────────────────────────────────────────── */
function ClinicInfo({ settings, reload }) {
  const [form, setForm] = useState({
    clinic_name: "", clinic_phone: "", clinic_address: "", clinic_email: "", vat_percent: 0, timezone: "Europe/Tirane",
    clinic_open_time: "08:00", clinic_close_time: "18:00",
    clinic_break_start: "13:00", clinic_break_end: "14:00",
    clinic_saturday_open: "09:00", clinic_saturday_close: "13:00",
    clinic_open_saturday: true, clinic_open_sunday: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({
        clinic_name: settings.clinic_name || "",
        clinic_phone: settings.clinic_phone || "",
        clinic_address: settings.clinic_address || "",
        clinic_email: settings.clinic_email || "",
        vat_percent: settings.vat_percent || 0,
        timezone: settings.timezone || "Europe/Tirane",
        clinic_open_time: settings.clinic_open_time || "08:00",
        clinic_close_time: settings.clinic_close_time || "18:00",
        clinic_break_start: settings.clinic_break_start || "13:00",
        clinic_break_end: settings.clinic_break_end || "14:00",
        clinic_saturday_open: settings.clinic_saturday_open || "09:00",
        clinic_saturday_close: settings.clinic_saturday_close || "13:00",
        clinic_open_saturday: settings.clinic_open_saturday !== false,
        clinic_open_sunday: !!settings.clinic_open_sunday,
      });
    }
  }, [settings]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      await reload();
      toast.success("Të dhënat e klinikës u ruajtën");
    } catch {
      toast.error("Gabim gjatë ruajtjes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="surface-card p-6 space-y-4 max-w-2xl" data-testid="clinic-info-form">
      <h2 className="text-lg font-semibold mb-2">Të dhënat e Klinikës</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="label-eyebrow">Emri i Klinikës</Label>
          <Input className="mt-2" value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} data-testid="clinic-name-input" />
        </div>
        <div>
          <Label className="label-eyebrow">Telefoni</Label>
          <Input className="mt-2" value={form.clinic_phone} onChange={(e) => setForm({ ...form, clinic_phone: e.target.value })} data-testid="clinic-phone-input" />
        </div>
        <div className="md:col-span-2">
          <Label className="label-eyebrow">Adresa</Label>
          <Input className="mt-2" value={form.clinic_address} onChange={(e) => setForm({ ...form, clinic_address: e.target.value })} data-testid="clinic-address-input" />
        </div>
        <div>
          <Label className="label-eyebrow">Email</Label>
          <Input type="email" className="mt-2" value={form.clinic_email} onChange={(e) => setForm({ ...form, clinic_email: e.target.value })} data-testid="clinic-email-input" />
        </div>
        <div>
          <Label className="label-eyebrow">TVSH (%)</Label>
          <Input type="number" step="0.01" className="mt-2" value={form.vat_percent} onChange={(e) => setForm({ ...form, vat_percent: Number(e.target.value) })} data-testid="clinic-vat-input" />
        </div>
        <div>
          <Label className="label-eyebrow">Zona Kohore</Label>
          <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
            <SelectTrigger className="mt-2" data-testid="clinic-tz-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Europe/Tirane">Europe/Tirane (Shqipëri)</SelectItem>
              <SelectItem value="Europe/Rome">Europe/Rome (Itali)</SelectItem>
              <SelectItem value="Europe/London">Europe/London (UK)</SelectItem>
              <SelectItem value="Europe/Athens">Europe/Athens (Greqi)</SelectItem>
              <SelectItem value="Europe/Berlin">Europe/Berlin (Gjermani)</SelectItem>
              <SelectItem value="Europe/Paris">Europe/Paris (Francë)</SelectItem>
              <SelectItem value="America/New_York">America/New_York (SHBA-E)</SelectItem>
              <SelectItem value="UTC">UTC</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="pt-2">
        <button type="submit" className="btn-primary" disabled={saving} data-testid="save-clinic-button">
          <Save size={14} /> {saving ? "Duke ruajtur…" : "Ruaj"}
        </button>
      </div>

      <div className="pt-6 border-t border-[#E2DFD8]">
        <h3 className="text-base font-semibold mb-3">Orari i Klinikës</h3>
        <p className="text-xs text-[#5C5C5C] mb-4">
          Përdoret te rezervimet online dhe AI Chat. Oraret e zëna dhe pushimi i drekës do të shfaqen të bllokuara.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="label-eyebrow">Hapje (Hënë–Premte)</Label>
            <Input type="time" className="mt-2" value={form.clinic_open_time}
                   onChange={(e) => setForm({ ...form, clinic_open_time: e.target.value })}
                   data-testid="clinic-open-time" />
          </div>
          <div>
            <Label className="label-eyebrow">Mbyllje (Hënë–Premte)</Label>
            <Input type="time" className="mt-2" value={form.clinic_close_time}
                   onChange={(e) => setForm({ ...form, clinic_close_time: e.target.value })}
                   data-testid="clinic-close-time" />
          </div>
          <div>
            <Label className="label-eyebrow">Pushim drekë (fillim)</Label>
            <Input type="time" className="mt-2" value={form.clinic_break_start}
                   onChange={(e) => setForm({ ...form, clinic_break_start: e.target.value })}
                   data-testid="clinic-break-start" />
          </div>
          <div>
            <Label className="label-eyebrow">Pushim drekë (mbarim)</Label>
            <Input type="time" className="mt-2" value={form.clinic_break_end}
                   onChange={(e) => setForm({ ...form, clinic_break_end: e.target.value })}
                   data-testid="clinic-break-end" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="rounded-lg border border-[#E2DFD8] p-3 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={form.clinic_open_saturday}
                     onChange={(e) => setForm({ ...form, clinic_open_saturday: e.target.checked })}
                     data-testid="clinic-open-saturday" />
              Hapur të shtunën
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Input type="time" value={form.clinic_saturday_open}
                     onChange={(e) => setForm({ ...form, clinic_saturday_open: e.target.value })}
                     disabled={!form.clinic_open_saturday}
                     data-testid="clinic-saturday-open" />
              <Input type="time" value={form.clinic_saturday_close}
                     onChange={(e) => setForm({ ...form, clinic_saturday_close: e.target.value })}
                     disabled={!form.clinic_open_saturday}
                     data-testid="clinic-saturday-close" />
            </div>
          </div>
          <div className="rounded-lg border border-[#E2DFD8] p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={form.clinic_open_sunday}
                     onChange={(e) => setForm({ ...form, clinic_open_sunday: e.target.checked })}
                     data-testid="clinic-open-sunday" />
              Hapur të dielën
            </label>
            <p className="text-xs text-[#5C5C5C]">
              Të dielën përdor të njëjtin orar Hënë–Premte.
            </p>
          </div>
        </div>

        <div className="pt-4">
          <button type="submit" className="btn-primary" disabled={saving} data-testid="save-hours-button">
            <Save size={14} /> {saving ? "Duke ruajtur…" : "Ruaj orarin"}
          </button>
        </div>
      </div>
    </form>
  );
}

/* ─────────────────────────────────────────────
   3) Users (existing functionality)
   ───────────────────────────────────────────── */
function UsersAdmin({ me }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // user being edited (for WA phone)
  const [form, setForm] = useState({ email: "", name: "", role: "doctor", password: "", phone_whatsapp: "" });

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    // Client-side validation BEFORE call
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Email-i është i pavlefshëm. P.sh.: era@klinika.al", { duration: 6000 });
      return;
    }
    if (!form.name || form.name.trim().length < 2) {
      toast.error("Emri është i detyrueshëm (të paktën 2 shkronja).", { duration: 6000 });
      return;
    }
    if (!form.password || form.password.length < 6) {
      toast.error("Fjalëkalimi duhet të jetë të paktën 6 karaktere.", { duration: 6000 });
      return;
    }
    try {
      const { data } = await api.post("/users", form);
      toast.success(`✅ ${data.name} u shtua me sukses!`, { duration: 5000 });
      setOpen(false);
      setForm({ email: "", name: "", role: "doctor", password: "", phone_whatsapp: "" });
      await load();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Krijimi dështoi. Provoni përsëri.";
      toast.error("❌ " + msg, { duration: 8000 });
    }
  };

  const saveWaPhone = async (userId, newPhone) => {
    try {
      await api.put(`/users/${userId}`, { phone_whatsapp: newPhone });
      toast.success("Numri WhatsApp u ruajt");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Fshini këtë përdorues?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("U fshi");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-section">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-lg font-semibold">Përdoruesit</h2>
          <p className="text-xs text-[#5C5C5C] mt-1" data-testid="users-count">
            Total: <b>{users.length}</b> përdorues
            {users.length > 0 && ` · ${users.filter(u => u.role === 'doctor').length} doktor(ë) · ${users.filter(u => u.role === 'receptionist').length} recepsionist(ë)`}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary" data-testid="add-user-button">
              <Plus size={16} /> Përdorues i Ri
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader><DialogTitle>Shto Përdorues</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label className="label-eyebrow">Emri i Plotë</Label>
                <Input value={form.name} required onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" data-testid="user-name-input" />
              </div>
              <div>
                <Label className="label-eyebrow">Email</Label>
                <Input type="email" value={form.email} required onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" data-testid="user-email-input" />
              </div>
              <div>
                <Label className="label-eyebrow">Roli</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-2" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Fjalëkalimi</Label>
                <Input type="password" value={form.password} required minLength={6}
                       onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2" data-testid="user-password-input" />
              </div>
              <div>
                <Label className="label-eyebrow">WhatsApp (opsionale, p.sh. +355677372080)</Label>
                <Input value={form.phone_whatsapp} placeholder="+355..."
                       onChange={(e) => setForm({ ...form, phone_whatsapp: e.target.value })} className="mt-2" data-testid="user-wa-input" />
                <p className="text-[10px] text-[#5C5C5C] mt-1">Doktori do marrë lajmërim WhatsApp për çdo takim të ri.</p>
              </div>
              <DialogFooter>
                <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Anulo</button>
                <button type="submit" className="btn-primary" data-testid="user-save-button">Krijo</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-left">
            <tr>
              <th className="px-6 py-3 label-eyebrow">Emri</th>
              <th className="px-6 py-3 label-eyebrow">Email</th>
              <th className="px-6 py-3 label-eyebrow">Roli</th>
              <th className="px-6 py-3 label-eyebrow">WhatsApp</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#E2DFD8]" data-testid={`user-row-${u.id}`}>
                <td className="px-6 py-4">{u.name}</td>
                <td className="px-6 py-4 font-mono text-xs">{u.email}</td>
                <td className="px-6 py-4">
                  <span className="rounded-full bg-[#EAE4D9] px-3 py-1 text-xs font-medium text-[#2C4C3B]">
                    {ROLES.find((r) => r.v === u.role)?.label || u.role}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {editing === u.id ? (
                    <WaPhoneEditor
                      initial={u.phone_whatsapp || ""}
                      onSave={(v) => saveWaPhone(u.id, v)}
                      onCancel={() => setEditing(null)}
                    />
                  ) : (
                    <button onClick={() => setEditing(u.id)} className="font-mono text-xs hover:text-[#2C4C3B]" data-testid={`edit-wa-${u.id}`}>
                      {u.phone_whatsapp || <span className="text-[#9E3C3C]">— shto</span>}
                    </button>
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  {u.id !== me?.id && (
                    <button onClick={() => remove(u.id)} className="text-[#9E3C3C] hover:bg-[#9E3C3C]/10 p-2 rounded-full" data-testid={`delete-user-${u.id}`}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────
   4) Email SMTP
   ───────────────────────────────────────────── */
function EmailSmtpConfig({ settings, reload }) {
  const [form, setForm] = useState({
    smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "",
    smtp_from_email: "", smtp_from_name: "DentArt",
    smtp_use_tls: true, smtp_enabled: false,
  });
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({
        smtp_host: settings.smtp_host || "",
        smtp_port: settings.smtp_port || 587,
        smtp_user: settings.smtp_user || "",
        smtp_password: settings.smtp_password || "",
        smtp_from_email: settings.smtp_from_email || "",
        smtp_from_name: settings.smtp_from_name || "DentArt",
        smtp_use_tls: settings.smtp_use_tls ?? true,
        smtp_enabled: settings.smtp_enabled ?? false,
      });
    }
  }, [settings]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      await reload();
      toast.success("Cilësimet SMTP u ruajtën");
    } catch {
      toast.error("Gabim gjatë ruajtjes");
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Vendos email për testim");
    setTesting(true);
    try {
      const { data } = await api.post("/notifications/test-email", {
        to: testTo,
        subject: "Test nga DentArt",
        body: "<p>Ky është një <b>email testi</b> nga sistemi juaj DentArt.</p>",
      });
      if (data.sent) toast.success("Email u dërgua! Kontrollo kutinë.");
      else toast.error("Nuk u dërgua: " + (data.reason || "gabim i panjohur"));
    } catch (e) {
      toast.error("Gabim: " + (e.response?.data?.detail || e.message));
    } finally { setTesting(false); }
  };

  return (
    <form onSubmit={submit} className="surface-card p-6 space-y-4 max-w-3xl" data-testid="smtp-form">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Email (SMTP i Personalizuar)</h2>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.smtp_enabled} onChange={(e) => setForm({ ...form, smtp_enabled: e.target.checked })} data-testid="smtp-enabled" />
          <span>Aktiv</span>
        </label>
      </div>
      <p className="text-sm text-[#5C5C5C]">Konfiguro serverin e tëndë SMTP (p.sh. Gmail, Outlook, Mailgun, ZeptoMail) për të dërguar automatikisht konfirmime takimesh.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="SMTP Host"><Input value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} placeholder="smtp.gmail.com" data-testid="smtp-host" /></Field>
        <Field label="Port"><Input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: Number(e.target.value) })} data-testid="smtp-port" /></Field>
        <Field label="Përdoruesi (email)"><Input value={form.smtp_user} onChange={(e) => setForm({ ...form, smtp_user: e.target.value })} placeholder="klinika@gmail.com" data-testid="smtp-user" /></Field>
        <Field label="Fjalëkalimi / App Password"><Input type="password" value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} data-testid="smtp-pass" /></Field>
        <Field label='"Nga" — Email'><Input value={form.smtp_from_email} onChange={(e) => setForm({ ...form, smtp_from_email: e.target.value })} placeholder="noreply@klinika.al" data-testid="smtp-from-email" /></Field>
        <Field label='"Nga" — Emri'><Input value={form.smtp_from_name} onChange={(e) => setForm({ ...form, smtp_from_name: e.target.value })} data-testid="smtp-from-name" /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={form.smtp_use_tls} onChange={(e) => setForm({ ...form, smtp_use_tls: e.target.checked })} data-testid="smtp-tls" />
        <span>Përdor STARTTLS (rekomanduar)</span>
      </label>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E2DFD8]">
        <button type="submit" className="btn-primary" disabled={saving} data-testid="save-smtp"><Save size={14} /> {saving ? "Duke ruajtur…" : "Ruaj"}</button>
        <div className="ml-auto flex items-center gap-2">
          <Input placeholder="email për testim…" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="smtp-test-to" className="w-64" />
          <button type="button" onClick={sendTest} disabled={testing} className="btn-ghost" data-testid="smtp-test-send">{testing ? "Duke dërguar…" : "Dërgo test"}</button>
        </div>
      </div>
    </form>
  );
}

/* ─────────────────────────────────────────────
   5) WhatsApp
   ───────────────────────────────────────────── */
function WhatsAppConfig({ settings, reload }) {
  const [form, setForm] = useState({
    wa_enabled: false, wa_method: "wa_me",
    wa_business_phone: "", wa_cloud_token: "", wa_phone_number_id: "",
    twilio_account_sid: "", twilio_auth_token: "", twilio_whatsapp_from: "whatsapp:+14155238886",
    twilio_appointment_template_sid: "",
    twilio_reminder_template_sid: "",
    twilio_doctor_alert_template_sid: "",
    twilio_doctor_agenda_template_sid: "",
    twilio_doctor_change_template_sid: "",
    wa_signature: "Dentini - Inteligjenca Artificiale - Zani Dental",
    wa_followup_enabled: true,
    wa_followup_text: "*Ky SMS u dergua nga Dentini - Inteligjenca Artificiale - Zani Dental*",
  });
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [testForceFreeform, setTestForceFreeform] = useState(true);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (settings) {
      setForm({
        wa_enabled: settings.wa_enabled ?? false,
        wa_method: settings.wa_method || "wa_me",
        wa_business_phone: settings.wa_business_phone || "",
        wa_cloud_token: settings.wa_cloud_token || "",
        wa_phone_number_id: settings.wa_phone_number_id || "",
        twilio_account_sid: settings.twilio_account_sid || "",
        twilio_auth_token: settings.twilio_auth_token || "",
        twilio_whatsapp_from: settings.twilio_whatsapp_from || "whatsapp:+14155238886",
        twilio_appointment_template_sid: settings.twilio_appointment_template_sid || "",
        twilio_reminder_template_sid: settings.twilio_reminder_template_sid || "",
        twilio_doctor_alert_template_sid: settings.twilio_doctor_alert_template_sid || "",
        twilio_doctor_agenda_template_sid: settings.twilio_doctor_agenda_template_sid || "",
        twilio_doctor_change_template_sid: settings.twilio_doctor_change_template_sid || "",
        wa_signature: settings.wa_signature ?? "Dentini - Inteligjenca Artificiale - Zani Dental",
        wa_followup_enabled: settings.wa_followup_enabled ?? true,
        wa_followup_text: settings.wa_followup_text ?? "*Ky SMS u dergua nga Dentini - Inteligjenca Artificiale - Zani Dental*",
      });
    }
  }, [settings]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      await reload();
      toast.success("Cilësimet WhatsApp u ruajtën");
    } catch { toast.error("Gabim"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testPhone) return toast.error("Vendos numrin për test");
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/notifications/test-whatsapp", {
        phone: testPhone,
        text: "🧪 Mesazh testi nga DentArt. Nëse e shihni këtë, integrimi WhatsApp funksionon!",
        force_freeform: testForceFreeform,
      });
      setTestResult(data);
      const finalStatus = data.delivery_status || (data.sent ? "queued" : "failed");
      if (finalStatus === "delivered" || finalStatus === "read") {
        toast.success(`✅ Mesazhi u dërgua dhe arriti! (${finalStatus})`);
      } else if (finalStatus === "sent" || finalStatus === "queued") {
        toast.info(`📤 Mesazhi është në rrugë (${finalStatus}). Verifikoni WhatsApp.`);
      } else {
        toast.error(`❌ Mesazhi NUK arriti: ${finalStatus}${data.delivery_error_code ? ' (kodi '+data.delivery_error_code+')' : ''}`);
      }
    } catch (e) {
      toast.error("Gabim: " + (e.response?.data?.detail || e.message));
    } finally { setTesting(false); }
  };

  const [sendingAgenda, setSendingAgenda] = useState(false);
  const [agendaResult, setAgendaResult] = useState(null);
  const sendAgendaNow = async () => {
    if (!window.confirm("Dërgo axhendën e sotme te të gjithë doktorët menjëherë?")) return;
    setSendingAgenda(true);
    setAgendaResult(null);
    try {
      const { data } = await api.post("/notifications/run-agenda-now");
      setAgendaResult(data);
      const sentCount = (data.results || []).filter(r => r.sent).length;
      const totalCount = (data.results || []).length;
      if (sentCount === totalCount && sentCount > 0) {
        toast.success(`📤 Axhenda u dërgua te ${sentCount}/${totalCount} doktorë. Kontrolloni rezultatet poshtë.`);
      } else if (sentCount > 0) {
        toast.warning(`⚠️ Axhenda u dërgua vetëm te ${sentCount}/${totalCount} doktorë. Shih detajet poshtë.`);
      } else {
        toast.error(`❌ Asnjë dërgim s'u krye. Shih detajet poshtë për arsyen.`);
      }
    } catch (e) {
      toast.error("Gabim: " + (e.response?.data?.detail || e.message));
    } finally { setSendingAgenda(false); }
  };

  return (
    <form onSubmit={submit} className="surface-card p-6 space-y-4 max-w-3xl" data-testid="wa-form">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">WhatsApp</h2>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.wa_enabled} onChange={(e) => setForm({ ...form, wa_enabled: e.target.checked })} data-testid="wa-enabled" />
          <span>Aktiv</span>
        </label>
      </div>

      <div>
        <Label className="label-eyebrow">Metoda</Label>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
          <button type="button" onClick={() => setForm({ ...form, wa_method: "wa_me" })} data-testid="wa-method-wa_me"
                  className={`text-left rounded-lg border-2 p-4 transition-colors ${form.wa_method === "wa_me" ? "border-[#2C4C3B] bg-[#2C4C3B]/5" : "border-[#E2DFD8]"}`}>
            <div className="font-semibold text-sm">📱 wa.me (Click-to-Send)</div>
            <div className="text-xs text-[#5C5C5C] mt-1">Gjeneron link të WhatsApp me mesazh të para-mbushur. Recepsionisti klikon. Falas, asnjë konfigurim.</div>
          </button>
          <button type="button" onClick={() => setForm({ ...form, wa_method: "twilio" })} data-testid="wa-method-twilio"
                  className={`text-left rounded-lg border-2 p-4 transition-colors ${form.wa_method === "twilio" ? "border-[#2C4C3B] bg-[#2C4C3B]/5" : "border-[#E2DFD8]"}`}>
            <div className="font-semibold text-sm">⚡ Twilio (Auto)</div>
            <div className="text-xs text-[#5C5C5C] mt-1">Automatike. Sandbox falas për testim (~5 min setup). Kosto në prodhim: ~$0.005-0.04/mesazh.</div>
          </button>
          <button type="button" onClick={() => setForm({ ...form, wa_method: "cloud_api" })} data-testid="wa-method-cloud_api"
                  className={`text-left rounded-lg border-2 p-4 transition-colors ${form.wa_method === "cloud_api" ? "border-[#2C4C3B] bg-[#2C4C3B]/5" : "border-[#E2DFD8]"}`}>
            <div className="font-semibold text-sm">🔵 Meta Cloud API</div>
            <div className="text-xs text-[#5C5C5C] mt-1">Falas deri 1000 biseda/muaj. Kërkon Meta Business + verifikim 1-2 ditë.</div>
          </button>
        </div>
      </div>

      <Field label="Numri Biznesi WhatsApp (përfshini kodin e shtetit, p.sh. +355691234567)">
        <Input value={form.wa_business_phone} onChange={(e) => setForm({ ...form, wa_business_phone: e.target.value })} placeholder="+355691234567" data-testid="wa-business-phone" />
      </Field>

      {form.wa_method === "twilio" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-lg bg-[#FDFBF7] p-4 border border-[#E2DFD8]">
          <div className="md:col-span-2 text-xs text-[#1A1A1A] bg-[#D4A24C]/15 border border-[#D4A24C]/40 rounded p-2.5">
            <b>📚 Si të testoni shpejt me Sandbox (5 minuta):</b><br/>
            1. Hapni <a href="https://www.twilio.com/console/sms/whatsapp/sandbox" target="_blank" rel="noreferrer" className="underline text-[#2C4C3B]">Twilio Console → WhatsApp Sandbox</a><br/>
            2. Kopjoni <b>Account SID</b> + <b>Auth Token</b> këtu poshtë<br/>
            3. Nga telefoni juaj (+355...), dërgoni <b>"join &lt;kod&gt;"</b> përmes WhatsApp te numri <b>+1 415 523 8886</b> (kodin e shihni në Console)<br/>
            4. Pas konfirmimit, mund t'ju dërgojmë mesazhe automatikisht në atë numër.
          </div>
          <Field label="Twilio Account SID"><Input type="password" value={form.twilio_account_sid} onChange={(e) => setForm({ ...form, twilio_account_sid: e.target.value })} placeholder="ACxxxxxxxx..." data-testid="twilio-sid" /></Field>
          <Field label="Twilio Auth Token"><Input type="password" value={form.twilio_auth_token} onChange={(e) => setForm({ ...form, twilio_auth_token: e.target.value })} data-testid="twilio-token" /></Field>
          <div className="md:col-span-2">
            <Field label='Numri "Nga" WhatsApp (Sandbox: whatsapp:+14155238886 — për prodhim përdor numrin tuaj të aprovuar)'>
              <Input value={form.twilio_whatsapp_from} onChange={(e) => setForm({ ...form, twilio_whatsapp_from: e.target.value })} placeholder="whatsapp:+14155238886" data-testid="twilio-from" />
            </Field>
          </div>

          <div className="md:col-span-2 mt-2 pt-3 border-t border-[#E2DFD8]">
            <div className="text-xs text-[#1A1A1A] bg-[#2C4C3B]/8 border border-[#2C4C3B]/30 rounded p-2.5 mb-3">
              <b>🎯 Për mesazhe automatike te pacientë të rinj (kërkesë e Meta-s):</b><br/>
              Kur sistemi krijon takim, mesazhi i WhatsApp duhet të dërgohet me një <b>Content Template të miratuar</b>.
              <br/>
              <a href="https://console.twilio.com/us1/develop/content/content-template-builder" target="_blank" rel="noreferrer" className="underline text-[#2C4C3B]">
                Krijo template-in te Twilio Console →
              </a> me Category=<b>Utility</b>, gjuhë <b>sq</b>, dhe trupin:<br/>
              <code className="block bg-white px-2 py-1.5 rounded my-1.5 text-[10px]">
                Përshëndetje &#123;&#123;1&#125;&#125;! Takimi juaj në klinikën &#123;&#123;2&#125;&#125; është konfirmuar për &#123;&#123;3&#125;&#125;. Ju mirëpresim!
              </code>
              Pas miratimit, kopjoni <b>Content SID</b> (HX…) këtu poshtë.
            </div>
            <Field label="Content Template SID për konfirmim takimi (opsionale, fillon me HX...)">
              <Input value={form.twilio_appointment_template_sid}
                     onChange={(e) => setForm({ ...form, twilio_appointment_template_sid: e.target.value })}
                     placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                     data-testid="twilio-template-sid" />
            </Field>
            <p className="text-[11px] text-[#5C5C5C] mt-1">
              Variablat e zëvendësimit automatik: <code>&#123;&#123;1&#125;&#125;</code> = emri i pacientit · <code>&#123;&#123;2&#125;&#125;</code> = emri i klinikës · <code>&#123;&#123;3&#125;&#125;</code> = data dhe ora në shqip
            </p>

            <div className="mt-4 grid grid-cols-1 gap-3">
              <div>
                <Field label="Template për KUJTUES PACIENTI (dërgohet automatikisht para takimit)">
                  <Input value={form.twilio_reminder_template_sid}
                         onChange={(e) => setForm({ ...form, twilio_reminder_template_sid: e.target.value })}
                         placeholder="HX..."
                         data-testid="twilio-reminder-sid" />
                </Field>
                <p className="text-[11px] text-[#5C5C5C] mt-1">
                  Trupi i propozuar (Utility, sq): <code className="block bg-white px-2 py-1 rounded mt-1 text-[10px]">
                    🔔 Kujtues nga &#123;&#123;2&#125;&#125;: Përshëndetje &#123;&#123;1&#125;&#125;! Ju kujtojmë takimin tuaj për &#123;&#123;3&#125;&#125;. Ju mirëpresim!
                  </code>
                  Logjikë: takim 09-12 → një ditë para në 17:00 · takim 15-19 → të njëjtën ditë në 10:00 · të tjerë → një ditë para në 17:00
                </p>
              </div>

              <div>
                <Field label="Template për DOKTORIN — TAKIM I RI (dërgohet pas çdo rezervimi)">
                  <Input value={form.twilio_doctor_alert_template_sid}
                         onChange={(e) => setForm({ ...form, twilio_doctor_alert_template_sid: e.target.value })}
                         placeholder="HX..."
                         data-testid="twilio-doctor-alert-sid" />
                </Field>
                <p className="text-[11px] text-[#5C5C5C] mt-1">
                  Trupi i propozuar: <code className="block bg-white px-2 py-1 rounded mt-1 text-[10px]">
                    🦷 Takim i Ri — Pacient: &#123;&#123;1&#125;&#125; · Tel: &#123;&#123;2&#125;&#125; · Arsyeja: &#123;&#123;3&#125;&#125; · Data: &#123;&#123;4&#125;&#125;
                  </code>
                </p>
              </div>

              <div>
                <Field label="Template për DOKTORIN — AXHENDA DITORE (08:00 çdo mëngjes)">
                  <Input value={form.twilio_doctor_agenda_template_sid}
                         onChange={(e) => setForm({ ...form, twilio_doctor_agenda_template_sid: e.target.value })}
                         placeholder="HX..."
                         data-testid="twilio-doctor-agenda-sid" />
                </Field>
                <p className="text-[11px] text-[#5C5C5C] mt-1">
                  Trupi i propozuar: <code className="block bg-white px-2 py-1 rounded mt-1 text-[10px]">
                    📅 Axhenda për &#123;&#123;1&#125;&#125; (&#123;&#123;3&#125;&#125; takim/e): &#123;&#123;2&#125;&#125;
                  </code>
                  Variablat: &#123;&#123;1&#125;&#125;=data, &#123;&#123;2&#125;&#125;=lista e takimeve, &#123;&#123;3&#125;&#125;=numri total
                </p>
              </div>

              <div>
                <Field label="Template për DOKTORIN — NDRYSHIM TAKIMI (anulim/spostim)">
                  <Input value={form.twilio_doctor_change_template_sid}
                         onChange={(e) => setForm({ ...form, twilio_doctor_change_template_sid: e.target.value })}
                         placeholder="HX..."
                         data-testid="twilio-doctor-change-sid" />
                </Field>
                <p className="text-[11px] text-[#5C5C5C] mt-1">
                  Trupi i propozuar (Utility, sq): <code className="block bg-white px-2 py-1 rounded mt-1 text-[10px]">
                    ⚠️ Ndryshim takimi — Pacienti &#123;&#123;1&#125;&#125; · Veprim: &#123;&#123;2&#125;&#125; · Nga: &#123;&#123;3&#125;&#125; · Në: &#123;&#123;4&#125;&#125;
                  </code>
                  Variablat: &#123;&#123;1&#125;&#125;=emër pacient, &#123;&#123;2&#125;&#125;=ANULUAR/SPOSTUAR/FSHIRE, &#123;&#123;3&#125;&#125;=ora e vjetër, &#123;&#123;4&#125;&#125;=ora e re ose "-".
                  Pa këtë template, lajmërimi për ndryshim nuk shkon jashtë dritares 24h — sistemi do rezervojë një link wa.me te <code>/lajmerime</code> që mund t'ia dërgoni doktorit me një klikim.
                </p>
              </div>
            </div>

            <div className="mt-4 text-xs bg-[#D4A24C]/15 border border-[#D4A24C]/40 rounded p-2.5">
              <b>💬 Riskedulimi nga WhatsApp aktivizohet automatikisht:</b> Pasi pacienti merr mesazhin e konfirmimit ose kujtuesin, ai mund të përgjigjet "ndrysho takimin për nesër në 14:00" dhe sistemi e riskedulon vetë (përdor AI Claude) dhe lajmëron doktorin. Konfiguro webhook-un Twilio te <code>POST /api/webhooks/twilio/inbound</code>.
            </div>
          </div>
        </div>
      )}

      {/* Nenshkrimi automatik i mesazheve freeform */}
      <div className="mt-6 rounded-lg bg-[#FDFBF7] p-4 border border-[#E2DFD8]">
        <div className="flex items-baseline justify-between mb-2">
          <h4 className="text-sm font-semibold tracking-tight">Nënshkrimi Automatik (Freeform)</h4>
          <span className="text-[10px] uppercase tracking-[0.15em] text-[#5C5C5C]">shfaqet në fund të çdo mesazhi bot AI</span>
        </div>
        <Field label="Teksti i nënshkrimit">
          <Input value={form.wa_signature}
                 onChange={(e) => setForm({ ...form, wa_signature: e.target.value })}
                 placeholder="Dentini - Inteligjenca Artificiale - Zani Dental"
                 data-testid="wa-signature-input" />
        </Field>
        <p className="text-[11px] text-[#5C5C5C] mt-2 leading-relaxed">
          Ky nënshkrim i shtohet automatikisht të gjitha mesazheve <b>freeform</b> të WhatsApp që dërgon
          sistemi — përgjigjet e AI-së, njoftimet e webhookut, dhe mesazhet manuale. Nuk mund t'i shtohet
          template-ave të miratuara nga Meta (Twilio Content Templates) sepse trupi i tyre është fiks.
          Për t'i pasur edhe template-at me nënshkrim, përfshijeni në trupin e template-it kur e krijoni te Twilio.
          Lëreni bosh për të hequr nënshkrimin.
        </p>
      </div>

      {/* Follow-up SMS pas template-eve */}
      <div className="mt-4 rounded-lg bg-[#FDFBF7] p-4 border border-[#E2DFD8]">
        <div className="flex items-baseline justify-between mb-2 gap-2 flex-wrap">
          <h4 className="text-sm font-semibold tracking-tight">Mesazh Follow-up pas Template-ave</h4>
          <label className="flex items-center gap-1.5 text-xs text-[#5C5C5C] cursor-pointer">
            <input type="checkbox"
                   checked={form.wa_followup_enabled}
                   onChange={(e) => setForm({ ...form, wa_followup_enabled: e.target.checked })}
                   className="accent-[#2C4C3B]"
                   data-testid="wa-followup-enabled" />
            <span>I aktivizuar</span>
          </label>
        </div>
        <Field label="Teksti i follow-up-it">
          <Input value={form.wa_followup_text}
                 onChange={(e) => setForm({ ...form, wa_followup_text: e.target.value })}
                 placeholder="*Ky SMS u dergua nga Dentini - Inteligjenca Artificiale - Zani Dental*"
                 disabled={!form.wa_followup_enabled}
                 data-testid="wa-followup-text" />
        </Field>
        <p className="text-[11px] text-[#5C5C5C] mt-2 leading-relaxed">
          Pas çdo mesazhi template të Twilio-s (konfirmim, kujtues, njoftim doktori, agjendë, ndryshim),
          sistemi dërgon një mesazh të dytë <b>freeform</b> me këtë tekst. Kjo mundëson që nënshkrimi
          <b> të arrijë te pacientët dhe doktorët gjithashtu</b>, kur ata janë brenda 24h të fundit
          nga mesazhi i tyre inbound më i fundit. Nëse jashtë 24h, Twilio kthen error 63016 dhe
          follow-up-i injorohet heshtazi (template-i kryesor delivered normalisht).
          <br />
          <span className="text-[#2C4C3B]">Konvencion WhatsApp: rrethimi me *asterisk* e bën tekstin <b>bold</b>.</span>
        </p>
      </div>

      {/* Wizard qe tregon si te perfshihet nenshkrimi te çdo template */}
      <div className="mt-4">
        <TemplateSignatureGuide signature={form.wa_signature} />
      </div>

      {form.wa_method === "cloud_api" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-lg bg-[#FDFBF7] p-4 border border-[#E2DFD8]">
          <Field label="Access Token (Meta)"><Input type="password" value={form.wa_cloud_token} onChange={(e) => setForm({ ...form, wa_cloud_token: e.target.value })} data-testid="wa-token" /></Field>
          <Field label="Phone Number ID"><Input value={form.wa_phone_number_id} onChange={(e) => setForm({ ...form, wa_phone_number_id: e.target.value })} data-testid="wa-pnid" /></Field>
          <div className="md:col-span-2 text-xs text-[#5C5C5C]">
            Merrni këto në <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="underline text-[#2C4C3B]">developers.facebook.com</a> → App → WhatsApp → API Setup.
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E2DFD8]">
        <button type="submit" className="btn-primary" disabled={saving} data-testid="save-wa"><Save size={14} /> {saving ? "Duke ruajtur…" : "Ruaj"}</button>
        {(form.wa_method === "twilio" || form.wa_method === "cloud_api") && form.wa_enabled && (
          <button
            type="button"
            onClick={sendAgendaNow}
            disabled={sendingAgenda}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border border-[#2C4C3B]/40 text-[#2C4C3B] hover:bg-[#2C4C3B]/10 disabled:opacity-50"
            data-testid="send-agenda-now"
            title="Dërgo axhendën e takimeve të sotme te të gjithë doktorët"
          >
            📅 {sendingAgenda ? "Duke dërguar…" : "Dërgo Axhendën Tani"}
          </button>
        )}
        {(form.wa_method === "twilio" || form.wa_method === "cloud_api") && (
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs text-[#5C5C5C] cursor-pointer">
              <input
                type="checkbox"
                checked={testForceFreeform}
                onChange={(e) => setTestForceFreeform(e.target.checked)}
                className="accent-[#2C4C3B]"
                data-testid="wa-test-force-freeform"
              />
              <span>Freeform (me nënshkrim)</span>
            </label>
            <Input placeholder="+355677372080" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} data-testid="wa-test-phone" className="w-48" />
            <button type="button" onClick={sendTest} disabled={testing} className="btn-ghost" data-testid="wa-test-send">{testing ? "Duke dërguar…" : "Dërgo test"}</button>
          </div>
        )}
      </div>

      {testResult && (
        <div className="rounded-lg border border-[#E2DFD8] bg-[#FAF8F2] p-3 text-xs space-y-1" data-testid="wa-test-result">
          <div className="font-semibold text-sm text-[#1A1A1A] mb-1">Rezultati i testit:</div>
          <div>API i pranoi: <b className={testResult.sent ? "text-[#2C4C3B]" : "text-[#9E3C3C]"}>{testResult.sent ? "Po (HTTP " + testResult.status + ")" : "Jo"}</b></div>
          {testResult.used && <div>Lloji i dërgimit: <b>{testResult.used === "template" ? "Template (business-initiated)" : "Freeform (24h window)"}</b></div>}
          {testResult.message_sid && <div>Twilio Message SID: <code>{testResult.message_sid}</code></div>}
          {testResult.delivery_status && (
            <div>Statusi i dorëzimit: <b className={
              testResult.delivery_status === "delivered" || testResult.delivery_status === "read" ? "text-[#2C4C3B]" :
              testResult.delivery_status === "failed" || testResult.delivery_status === "undelivered" ? "text-[#9E3C3C]" : "text-[#D4A24C]"
            }>{testResult.delivery_status}</b></div>
          )}
          {testResult.delivery_error_code && (
            <div className="text-[#9E3C3C]">Kodi i gabimit Twilio: <code>{testResult.delivery_error_code}</code>
              {testResult.delivery_error_code === 63016 && " — Pacienti nuk ka shkruar në 24h. Përdorni një template të miratuar."}
              {testResult.delivery_error_code === 63015 && " — Numri nuk është bashkuar me sandbox-in. Përdorni Sender LIVE ose përfundoni hapin 'join <kod>'."}
              {testResult.delivery_error_code === 63031 && " — Nuk mund të dërgoni nga e te i njëjti numër."}
              {testResult.delivery_error_code === 21656 && " — Template-i nuk ekziston ose nuk është i miratuar."}
            </div>
          )}
          {testResult.reason && !testResult.sent && <div className="text-[#9E3C3C]">Arsyeja: {testResult.reason}</div>}
          {testResult.followup && (
            <div className="mt-2 pt-2 border-t border-[#E2DFD8]">
              <div className="font-semibold text-sm text-[#1A1A1A] mb-1">Follow-up (nënshkrimi):</div>
              <div>API i pranoi: <b className={testResult.followup.sent ? "text-[#2C4C3B]" : "text-[#9E3C3C]"}>{testResult.followup.sent ? "Po (HTTP " + testResult.followup.status + ")" : "Jo"}</b></div>
              {testResult.followup.message_sid && <div>SID: <code>{testResult.followup.message_sid}</code></div>}
              {testResult.followup.delivery_status && (
                <div>Statusi i dorëzimit: <b className={
                  testResult.followup.delivery_status === "delivered" || testResult.followup.delivery_status === "read" ? "text-[#2C4C3B]" :
                  testResult.followup.delivery_status === "failed" || testResult.followup.delivery_status === "undelivered" ? "text-[#9E3C3C]" : "text-[#D4A24C]"
                }>{testResult.followup.delivery_status}</b></div>
              )}
              {testResult.followup.delivery_error_code && (
                <div className="text-[#9E3C3C]">
                  Kodi: <code>{testResult.followup.delivery_error_code}</code>
                  {testResult.followup.delivery_error_code === 63016 && " — Follow-up-i kërkon të jesh brenda 24h nga inbound-i i marrësit. Kërkoni marrësit të dërgojë një mesazh WhatsApp te klinika dhe brenda 24h ritestoni."}
                </div>
              )}
              {!testResult.followup.sent && testResult.followup.reason && (
                <div className="text-[#9E3C3C]">Arsyeja: {testResult.followup.reason}</div>
              )}
            </div>
          )}
        </div>
      )}

      {agendaResult && (
        <div className="rounded-lg border border-[#E2DFD8] bg-[#FAF8F2] p-3 text-xs space-y-2" data-testid="agenda-result">
          <div className="font-semibold text-sm text-[#1A1A1A]">Rezultati i Axhendës:</div>
          {agendaResult.settings && (
            <div className="text-[11px] text-[#5C5C5C] space-y-0.5">
              <div>wa_enabled: <b>{String(agendaResult.settings.wa_enabled)}</b> · metoda: <b>{agendaResult.settings.wa_method}</b></div>
              <div>twilio_from: <code>{agendaResult.settings.twilio_from || "—"}</code></div>
              <div>account_sid set: <b>{String(agendaResult.settings.twilio_account_sid_set)}</b> · auth_token set: <b>{String(agendaResult.settings.twilio_auth_token_set)}</b> · template SID set: <b>{String(agendaResult.settings.has_twilio_doctor_agenda_template)}</b></div>
            </div>
          )}
          {agendaResult.reason && !agendaResult.ok && (
            <div className="text-[#9E3C3C]"><b>Arsyeja e dështimit:</b> {agendaResult.reason}</div>
          )}
          {(agendaResult.results || []).length === 0 && agendaResult.ok && (
            <div className="text-[#9E3C3C]">Asnjë doktor me telefon WhatsApp të vlefshëm u gjet.</div>
          )}
          {(agendaResult.results || []).map((r, i) => (
            <div key={i} className="border-t border-[#E2DFD8] pt-2">
              <div className="font-medium">{r.doctor || "—"} <span className="text-[#5C5C5C] font-mono text-[11px]">{r.phone || "pa telefon"}</span></div>
              <div>Takime sot: <b>{r.appointments_count}</b> · Metoda: <code>{r.method_used || "—"}</code></div>
              <div>U dërgua: <b className={r.sent ? "text-[#2C4C3B]" : "text-[#9E3C3C]"}>{r.sent ? "PO" : "JO"}</b>
                {r.twilio_status && <> · HTTP: <code>{r.twilio_status}</code></>}
                {r.message_sid && <> · SID: <code>{r.message_sid.substring(0, 16)}...</code></>}
              </div>
              {r.delivery_status && (
                <div>Statusi i dorëzimit: <b className={
                  r.delivery_status === "delivered" || r.delivery_status === "read" ? "text-[#2C4C3B]" :
                  r.delivery_status === "failed" || r.delivery_status === "undelivered" ? "text-[#9E3C3C]" : "text-[#D4A24C]"
                }>{r.delivery_status}</b>
                  {r.delivery_error_code && <span className="text-[#9E3C3C] ml-2">Kodi: <code>{r.delivery_error_code}</code></span>}
                </div>
              )}
              {r.reason && <div className="text-[#9E3C3C]">Arsyeja: {r.reason}</div>}
              {(r.appointment_samples || []).length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[#5C5C5C]">Lista e takimeve të përfshira ({r.appointment_samples.length})</summary>
                  <ul className="mt-1 ml-3 list-disc text-[11px]">
                    {r.appointment_samples.map((s, j) => (
                      <li key={j}>{s.time} — {s.patient} ({s.reason}){!s.assigned && " [Pa caktuar]"}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </form>
  );
}

/* ─────────────────────────────────────────────
   6) AI Chat config
   ───────────────────────────────────────────── */
function AIChatConfig({ settings, reload }) {
  const [form, setForm] = useState({
    ai_enabled: true,
    ai_model: "claude-sonnet-4-5-20250929",
    ai_provider: "anthropic",
    ai_welcome_sq: "", ai_welcome_en: "", ai_welcome_it: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({
        ai_enabled: settings.ai_enabled ?? true,
        ai_model: settings.ai_model || "claude-sonnet-4-5-20250929",
        ai_provider: settings.ai_provider || "anthropic",
        ai_welcome_sq: settings.ai_welcome_sq || "",
        ai_welcome_en: settings.ai_welcome_en || "",
        ai_welcome_it: settings.ai_welcome_it || "",
      });
    }
  }, [settings]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      await reload();
      toast.success("Cilësimet AI u ruajtën");
    } catch { toast.error("Gabim"); }
    finally { setSaving(false); }
  };

  const setProvider = (prov) => {
    const defaults = {
      anthropic: "claude-sonnet-4-5-20250929",
      openai: "gpt-5.2",
      gemini: "gemini-2.5-pro",
    };
    setForm({ ...form, ai_provider: prov, ai_model: defaults[prov] });
  };

  return (
    <form onSubmit={submit} className="surface-card p-6 space-y-4 max-w-3xl" data-testid="ai-form">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">AI Asistent (Chat Multi-gjuhësh)</h2>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.ai_enabled} onChange={(e) => setForm({ ...form, ai_enabled: e.target.checked })} data-testid="ai-enabled" />
          <span>Aktiv</span>
        </label>
      </div>
      <p className="text-sm text-[#5C5C5C]">AI-ja njeh procedurat dhe çmimet e klinikës, përgjigjet në Shqip / English / Italiano, dhe mund të rezervojë takime direkt nga biseda.</p>

      <div>
        <Label className="label-eyebrow">Ofruesi i Modelit</Label>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {[
            { v: "anthropic", label: "Claude (Anthropic)", note: "Më i mirë në SHQ & IT" },
            { v: "openai", label: "GPT (OpenAI)", note: "I shpejtë & i gjerë" },
            { v: "gemini", label: "Gemini (Google)", note: "I ekonomik" },
          ].map((p) => (
            <button key={p.v} type="button" onClick={() => setProvider(p.v)} data-testid={`ai-prov-${p.v}`}
                    className={`text-left rounded-lg border-2 p-3 text-sm transition-colors ${form.ai_provider === p.v ? "border-[#2C4C3B] bg-[#2C4C3B]/5" : "border-[#E2DFD8]"}`}>
              <div className="font-medium">{p.label}</div>
              <div className="text-[10px] text-[#5C5C5C] mt-0.5">{p.note}</div>
            </button>
          ))}
        </div>
      </div>

      <Field label="ID e Modelit">
        <Input value={form.ai_model} onChange={(e) => setForm({ ...form, ai_model: e.target.value })} data-testid="ai-model" />
      </Field>

      <div className="grid grid-cols-1 gap-3">
        <Field label="Mirëseardhje — Shqip 🇦🇱"><Input value={form.ai_welcome_sq} onChange={(e) => setForm({ ...form, ai_welcome_sq: e.target.value })} data-testid="ai-welcome-sq" /></Field>
        <Field label="Welcome — English 🇬🇧"><Input value={form.ai_welcome_en} onChange={(e) => setForm({ ...form, ai_welcome_en: e.target.value })} data-testid="ai-welcome-en" /></Field>
        <Field label="Benvenuto — Italiano 🇮🇹"><Input value={form.ai_welcome_it} onChange={(e) => setForm({ ...form, ai_welcome_it: e.target.value })} data-testid="ai-welcome-it" /></Field>
      </div>

      <div className="pt-2 border-t border-[#E2DFD8]">
        <button type="submit" className="btn-primary" disabled={saving} data-testid="save-ai"><Save size={14} /> {saving ? "Duke ruajtur…" : "Ruaj"}</button>
      </div>
    </form>
  );
}

/* ─────────────────────────────────────────────
   7) Embed Code (drop-in <script> for external websites)
   ───────────────────────────────────────────── */
function EmbedCodeTab() {
  const origin = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const snippet = `<!-- DentArt AI Chat — drop into your website -->\n<script src="${origin}/embed.js" async></script>`;
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("U kopjua!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopjimi dështoi");
    }
  };

  return (
    <div className="surface-card p-6 space-y-4 max-w-3xl" data-testid="embed-tab">
      <h2 className="text-lg font-semibold">Embed në Website-in tuaj</h2>
      <p className="text-sm text-[#5C5C5C]">
        Kopjoni këtë rresht në fund të faqes ({"<"}/body{">"}) ku doni të shfaqet asistenti AI:
      </p>
      <pre className="bg-[#1A1A1A] text-[#FDFBF7] rounded-lg p-4 text-xs overflow-x-auto" data-testid="embed-snippet">{snippet}</pre>
      <button onClick={copy} className="btn-primary" data-testid="embed-copy">
        {copied ? "✓ U kopjua" : "Kopjo kodin"}
      </button>
      <div className="text-xs text-[#5C5C5C] pt-2 border-t border-[#E2DFD8]">
        Pasi ta vendosni në sitin tuaj, vizitorët do të shohin një buton notues "Asistenti Virtual" poshtë-djathtas. AI-ja njeh shërbimet dhe rezervon takime drejtpërdrejt në DentArt.
      </div>
    </div>
  );
}


function WaPhoneEditor({ initial, onSave, onCancel }) {
  const [v, setV] = useState(initial || "");
  return (
    <div className="flex items-center gap-1">
      <Input value={v} onChange={(e) => setV(e.target.value)} placeholder="+355..."
             className="h-8 text-xs font-mono w-40" data-testid="wa-phone-input" autoFocus />
      <button type="button" onClick={() => onSave(v.trim())} className="text-[#2C4C3B] p-1 rounded hover:bg-[#2C4C3B]/10" data-testid="wa-phone-save">
        <Save size={12} />
      </button>
      <button type="button" onClick={onCancel} className="text-[#5C5C5C] p-1 rounded hover:bg-[#5C5C5C]/10">
        ✕
      </button>
    </div>
  );
}
